import { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import type { Mesh, PointLight } from "three";
import type { FocusEffectAppearance } from "@chuzi/shared/realms/cosmos/components";

/**
 * Vibrant halo around the focused story anchor — layered glow with an intense pulse.
 */
export function StarFocusGlow({
  position,
  hue = 210,
  intensity = 0.5,
  yOffset = 0,
  focusEffect,
  variant = "default",
}: {
  position: [number, number, number];
  hue?: number;
  intensity?: number;
  yOffset?: number;
  focusEffect?: FocusEffectAppearance;
  variant?: "default" | "browse";
}) {
  const outerRef = useRef<Mesh>(null);
  const midRef = useRef<Mesh>(null);
  const innerRef = useRef<Mesh>(null);
  const lightRef = useRef<PointLight>(null);

  const isBrowse = variant === "browse";
  const effect = focusEffect?.enabled ? focusEffect : null;
  const glowScale = isBrowse ? 0.55 : (effect?.glowScale ?? 1);
  const opacityBoost = isBrowse ? 0.14 : (effect?.glowOpacityBoost ?? 0);

  const outerScale = isBrowse ? 1.4 : 3.2;
  const midScale = isBrowse ? 0.9 : 2.0;
  const innerScale = isBrowse ? 0.5 : 1.2;

  const saturation = isBrowse ? 95 : 82;
  const lightness = (isBrowse ? 68 : 58) + intensity * 24 + (effect ? 8 : 0);
  const color = `hsl(${((hue % 360) + 360) % 360}, ${saturation}%, ${lightness}%)`;
  const coreColor = `hsl(${((hue % 360) + 360) % 360}, 98%, ${lightness + 14}%)`;

  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    const breathe = 1 + Math.sin(t * 0.7) * (isBrowse ? 0.06 : 0.04);
    const shimmer = Math.sin(t * 1.2) * 0.5 + 0.5;
    const baseOuter = isBrowse ? 0.14 : 0.06;
    const outerOpacity = baseOuter + shimmer * (isBrowse ? 0.04 : 0.02) + opacityBoost;
    const midOpacity = outerOpacity * (isBrowse ? 1.8 : 1.6);
    const innerOpacity = outerOpacity * (isBrowse ? 2.8 : 2.4);

    if (outerRef.current) {
      outerRef.current.scale.setScalar(outerScale * glowScale * breathe);
      (outerRef.current.material as { opacity: number }).opacity = outerOpacity;
    }
    if (midRef.current) {
      midRef.current.scale.setScalar(midScale * glowScale * breathe);
      (midRef.current.material as { opacity: number }).opacity = midOpacity;
    }
    if (innerRef.current) {
      innerRef.current.scale.setScalar(innerScale * glowScale * breathe);
      (innerRef.current.material as { opacity: number }).opacity = innerOpacity;
    }
    if (lightRef.current && effect) {
      lightRef.current.intensity =
        effect.pointLightIntensity * 0.4 * (0.9 + Math.sin(t * 2.0) * 0.1);
    }
  });

  return (
    <group position={[position[0], position[1] + yOffset, position[2]]}>
      {effect ? (
        <pointLight
          ref={lightRef}
          color={coreColor}
          intensity={effect.pointLightIntensity}
          distance={24}
          decay={2}
        />
      ) : null}
      <mesh ref={outerRef}>
        <sphereGeometry args={[1, 20, 20]} />
        <meshBasicMaterial
          color={color}
          transparent
          opacity={isBrowse ? 0.2 : 0.12}
          toneMapped={false}
          depthWrite={false}
        />
      </mesh>
      <mesh ref={midRef}>
        <sphereGeometry args={[1, 20, 20]} />
        <meshBasicMaterial
          color={color}
          transparent
          opacity={isBrowse ? 0.32 : 0.2}
          toneMapped={false}
          depthWrite={false}
        />
      </mesh>
      <mesh ref={innerRef}>
        <sphereGeometry args={[1, 20, 20]} />
        <meshBasicMaterial
          color={coreColor}
          transparent
          opacity={isBrowse ? 0.45 : 0.28}
          toneMapped={false}
          depthWrite={false}
        />
      </mesh>
    </group>
  );
}
